import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";

import { ConnectionState } from "lib/consts/ARC/connection";
import { DefaultLightState, LightState } from "lib/consts/ARC/light";
import {
    parseSpotLightTopicPayload,
    setSpotLight,
    SPOT_LIGHT_TOPIC,
    spotLightStateFromPayload,
} from "lib/protocols/spotLight";
import {
    flashStateFromPayload,
    parseSecurityModuleTopicPayload,
    SECURITY_MODULE_TOPIC,
    setSecurityModule,
} from "lib/protocols/securityModule";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";

export const LightControlContext = createContext();
LightControlContext.displayName = "ARCLightControl";
export const useLightControl = () => useContext(LightControlContext);

const LightControlProvider = ({ children }) => {
    /** Spot light state from `/hardware/spot_light` (r2c2_controlboard feedback). */
    const [topicLightState, setTopicLightState] = useState(null);
    const [topicSecurityState, setTopicSecurityState] = useState(null);
    /** Immediate toggle feedback until ROS topic catches up. */
    const [uiLightOverride, setUiLightOverride] = useState(null);
    const [uiFlashOverride, setUiFlashOverride] = useState(null);
    const [isToggling, setIsToggling] = useState(false);
    const [isFlashToggling, setIsFlashToggling] = useState(false);
    /** Target state while waiting for hardware feedback to confirm a toggle. */
    const pendingLightStateRef = useRef(null);
    const pendingFlashStateRef = useRef(null);
    const { callService, connectionState, controlSocket } = useARC();
    const { addToast } = useToast();

    const lightState = uiLightOverride ?? topicLightState ?? DefaultLightState;
    const flashState =
        uiFlashOverride ??
        (topicSecurityState
            ? flashStateFromPayload(topicSecurityState)
            : DefaultLightState);
    const isSecurityModuleConnected = topicSecurityState?.connected === true;

    useEffect(() => {
        if (connectionState !== ConnectionState.Connected || !controlSocket) {
            setTopicLightState(null);
            setTopicSecurityState(null);
            setUiLightOverride(null);
            setUiFlashOverride(null);
            pendingLightStateRef.current = null;
            pendingFlashStateRef.current = null;
            return;
        }

        const cleanupSpot = attachRosTopicWithRetry(
            controlSocket,
            SPOT_LIGHT_TOPIC,
            (data) => {
                const parsed = parseSpotLightTopicPayload(data);
                if (!parsed) {
                    return;
                }
                const state = spotLightStateFromPayload(parsed);
                if (
                    pendingLightStateRef.current !== null &&
                    state !== pendingLightStateRef.current
                ) {
                    return;
                }
                setTopicLightState(state);
                if (pendingLightStateRef.current === state) {
                    pendingLightStateRef.current = null;
                    setUiLightOverride(null);
                }
            },
            {
                ...LATE_ROS_TOPIC_OPTIONS,
                markMessageReceived: (data) => parseSpotLightTopicPayload(data) !== null,
            }
        );
        const cleanupSecurity = attachRosTopicWithRetry(
            controlSocket,
            SECURITY_MODULE_TOPIC,
            (data) => {
                const parsed = parseSecurityModuleTopicPayload(data);
                if (!parsed) {
                    return;
                }
                setTopicSecurityState(parsed);
                const state = flashStateFromPayload(parsed);
                if (
                    pendingFlashStateRef.current !== null &&
                    state !== pendingFlashStateRef.current
                ) {
                    return;
                }
                if (pendingFlashStateRef.current === state) {
                    pendingFlashStateRef.current = null;
                    setUiFlashOverride(null);
                }
            },
            {
                ...LATE_ROS_TOPIC_OPTIONS,
                subscribeQos: {
                    durability: "transient_local",
                    reliability: "reliable",
                    depth: 1,
                },
                markMessageReceived: (data) =>
                    parseSecurityModuleTopicPayload(data) !== null,
            }
        );

        return () => {
            cleanupSpot();
            cleanupSecurity();
        };
    }, [controlSocket, connectionState]);

    const toggleLight = useCallback(async () => {
        if (connectionState !== ConnectionState.Connected || isToggling) return;
        const nextOn = lightState !== LightState.ON;
        const targetState = nextOn ? LightState.ON : LightState.OFF;
        pendingLightStateRef.current = targetState;
        setUiLightOverride(targetState);
        setIsToggling(true);
        try {
            const res = await callService(
                ...setSpotLight(nextOn, nextOn ? 100 : 0)
            );
            if (res && res.success === false) {
                throw new Error(res.message || "Spot light command failed");
            }
        } catch (e) {
            pendingLightStateRef.current = null;
            setUiLightOverride(null);
            const msg = e instanceof Error ? e.message : "Spot light command failed";
            addToast({ type: "error", message: msg });
        } finally {
            setIsToggling(false);
        }
    }, [addToast, callService, connectionState, isToggling, lightState]);

    const toggleFlash = useCallback(async () => {
        if (connectionState !== ConnectionState.Connected || isFlashToggling) return;
        const nextOn = flashState !== LightState.ON;
        const targetState = nextOn ? LightState.ON : LightState.OFF;
        const spotlightOn =
            topicSecurityState?.spotlight ?? lightState === LightState.ON;
        pendingFlashStateRef.current = targetState;
        setUiFlashOverride(targetState);
        setIsFlashToggling(true);
        try {
            const res = await callService(
                ...setSecurityModule(spotlightOn, nextOn)
            );
            if (res && res.success === false) {
                throw new Error(res.message || "Flash command failed");
            }
        } catch (e) {
            pendingFlashStateRef.current = null;
            setUiFlashOverride(null);
            const msg = e instanceof Error ? e.message : "Flash command failed";
            addToast({ type: "error", message: msg });
        } finally {
            setIsFlashToggling(false);
        }
    }, [
        addToast,
        callService,
        connectionState,
        flashState,
        isFlashToggling,
        lightState,
        topicSecurityState,
    ]);

    return (
        <LightControlContext.Provider
            value={{
                lightState,
                toggleLight,
                isToggling,
                flashState,
                toggleFlash,
                isFlashToggling,
                isSecurityModuleConnected,
            }}
        >
            {children}
        </LightControlContext.Provider>
    );
};

export default LightControlProvider;
